import clsx from 'clsx';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Need as ApiNeed } from '../../../../../js/api/models/Need';
import { StatusPill } from '../../../components/StatusPill';
import { TooltippedText } from '../../../components/tooltipped-text';
import {
  URGENCY_LEVEL_STYLE_MAP,
  URGENCY_LEVEL_ICONS,
  DATA_BADGE_STYLES,
} from '../../../utils/dataStyles';
import { formatApiDate } from '../../../utils/date';

const CardHeading: React.FC<React.ComponentPropsWithoutRef<'p'>> = ({
  className,
  children,
  ...props
}) => {
  return (
    <p
      {...props}
      className={clsx(
        'tw:text-sm tw:font-semibold tw:text-zinc-900 tw:dark:text-zinc-100 tw:!mb-0',
        className
      )}
    >
      {children}
    </p>
  );
};

export const NeedCard: React.FC<{
  need: ApiNeed;
  degreeMap: Record<string, string>;
  specialtyMap: Record<string, string>;
  shiftRateTypeMap: Record<string, string>;
  shiftRateTypeIconMap: Record<string, string>;
  justificationMap: Record<string, string>;
  urgencyLevelMap: Record<string, string>;
}> = ({
  need,
  degreeMap,
  specialtyMap,
  shiftRateTypeMap,
  shiftRateTypeIconMap,
  justificationMap,
  urgencyLevelMap,
}) => {
  const navigate = useNavigate();

  const handleViewClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/app/organization/needs/${need.id}`);
  };

  const urgencyStyle =
    need.urgency_level && URGENCY_LEVEL_STYLE_MAP[need.urgency_level]
      ? URGENCY_LEVEL_STYLE_MAP[need.urgency_level]
      : DATA_BADGE_STYLES.DEFAULT;
  const urgencyIcon =
    need.urgency_level && URGENCY_LEVEL_ICONS[need.urgency_level]
      ? URGENCY_LEVEL_ICONS[need.urgency_level]
      : URGENCY_LEVEL_ICONS.ROUTINE;

  return (
    <div className="tw:h-full tw:bg-white tw:rounded-lg tw:shadow-sm tw:border tw:border-zinc-200 tw:border-l-brand-green-2 tw:border-l-6 tw:hover:shadow-md tw:hover:bg-muted tw:transition-shadow tw:cursor-pointer tw:dark:bg-zinc-700 tw:dark:border-zinc-400 tw:dark:hover:bg-zinc-600">
      <div className="tw:p-3">
        <div className="tw:flex tw:justify-between tw:items-center tw:mb-2">
          <CardHeading className="">
            <i className="far fa-fw fa-lg fa-users-medical tw:mr-1" />
            {need.code || 'Untitled Need'}
          </CardHeading>
          <div className="tw:flex tw:items-center tw:gap-2">
            <StatusPill
              status={need.current_status?.title || 'Unknown'}
              category={need.current_status?.category}
              text={
                need.current_reason
                  ? `${need.current_status?.title}: ${need.current_reason?.title}`
                  : undefined
              }
            />
          </div>
        </div>
        <div className="tw:flex tw:flex-wrap tw:gap-2 tw:mb-3">
          <span className="tw:bg-zinc-100 tw:border tw:border-zinc-300 tw:text-brand-green-0 tw:text-xs tw:px-2 tw:py-1 tw:rounded-full tw:dark:bg-brand-green-1/20 tw:dark:border-brand-green-1/30 tw:dark:text-brand-green-1">
            {specialtyMap[need.specialty] || need.specialty}
          </span>
          {need.degrees?.map((deg) => (
            <span
              key={deg}
              className="tw:bg-zinc-100 tw:border tw:border-zinc-300 tw:text-zinc-700 tw:text-xs tw:px-2 tw:py-1 tw:rounded-full tw:dark:bg-zinc-600 tw:dark:border-zinc-500 tw:dark:text-zinc-300"
            >
              {degreeMap[deg] || deg}
            </span>
          ))}
        </div>

        <div className="tw:text-sm tw:text-zinc-600 tw:mb-3 tw:dark:text-zinc-300">
          <div className="tw:flex tw:items-center tw:mb-1">
            <i className="fas fa-fw fa-hospital tw:mr-2 tw:text-zinc-400 tw:dark:text-zinc-500" />
            <span className="tw:font-medium">{need.facility?.name}</span>
          </div>
          {need.facility?.city && need.facility?.state && (
            <div className="tw:flex tw:items-center tw:mb-1">
              <i className="fas fa-fw fa-map-marker-alt tw:mr-2 tw:text-zinc-400 tw:dark:text-zinc-500" />
              <span>
                {need.facility.city}, {need.facility.state}
              </span>
            </div>
          )}
          {need.target_on && (
            <div className="tw:flex tw:items-center tw:mb-1">
              <i className="fas fa-fw fa-calendar tw:mr-2 tw:text-zinc-400 tw:dark:text-zinc-500" />
              <span>Target: {formatApiDate(need.target_on)}</span>
            </div>
          )}
          {need.num_shifts && (
            <div className="tw:flex tw:items-center tw:mb-1">
              <i className="fas fa-fw fa-hashtag tw:mr-2 tw:text-zinc-400 tw:dark:text-zinc-500" />
              <span>Unfilled Shifts: {need.num_shifts}</span>
            </div>
          )}
        </div>
        {/* Shift Rate Types */}
        {need.shift_rate_types && need.shift_rate_types.length > 0 && (
          <div className="tw:flex tw:flex-row tw:flex-wrap tw:align-center tw:items-center tw:gap-3 tw:mb-3">
            {need.shift_rate_types.map((shiftType) => {
              // Map shift type to max_rate property
              const shiftTypeToRateKey: Record<string, string> = {
                day: 'max_rate_day',
                night: 'max_rate_night',
                call: 'max_rate_call',
                '24h': 'max_rate_24h',
              };
              const rateKey = shiftTypeToRateKey[shiftType] as keyof typeof need;
              const rate = need[rateKey];
              const label = shiftRateTypeMap[shiftType] || shiftType;
              const tooltip = rate ? `Max Rate: $${rate}` : 'Max rate not set';
              return (
                <React.Fragment key={shiftType}>
                  <TooltippedText tooltipContent={tooltip} placement="bottom">
                    <div className="tw:bg-white tw:border tw:border-zinc-300 tw:text-xs tw:px-2 tw:py-1 tw:rounded-full tw:flex tw:items-center tw:gap-1 tw:dark:bg-zinc-600 tw:dark:border-zinc-500">
                      <i
                        className={`fas fa-fw ${shiftRateTypeIconMap[shiftType] || 'fa-question'} tw:text-zinc-500 tw:dark:text-zinc-400`}
                      />
                      <span className="tw:text-zinc-500 tw:dark:text-zinc-400">{label}</span>
                    </div>
                  </TooltippedText>
                </React.Fragment>
              );
            })}
          </div>
        )}
        {/* Urgency Level */}
        {(need.urgency_level || need.justification) && (
          <div className="tw:mb-0">
            <span
              className={clsx(
                urgencyStyle,
                'tw:text-xs tw:px-2 tw:py-1 tw:rounded-full tw:border tw:inline-block tw:max-w-full tw:truncate'
              )}
            >
              {need.urgency_level && <i className={clsx(urgencyIcon, 'tw:mr-1')} />}
              {(() => {
                const urgencyLabel =
                  need.urgency_level && (urgencyLevelMap[need.urgency_level] || need.urgency_level);
                const justificationLabel =
                  need.justification &&
                  (justificationMap[need.justification] || need.justification);

                return (
                  <>
                    {urgencyLabel}
                    {urgencyLabel && justificationLabel && ': '}
                    {justificationLabel}
                  </>
                );
              })()}
            </span>
          </div>
        )}
        {/* Work Types */}
        {/* {need.work_types && need.work_types.length > 0 && (
          <div className="tw:text-sm tw:text-zinc-600 tw:mb-2 tw:dark:text-zinc-300">
            <span className="tw:font-medium">Work Types: </span>
            {need.work_types.join(', ')}
          </div>
        )} */}

        {/* Job Requisition Info */}
        {/* {(need.job_requisition_key || need.job_requisition_url) && (
          <div className="tw:text-sm tw:text-zinc-600 tw:mb-2 tw:dark:text-zinc-300">
            <span className="tw:font-medium">Job Requisition: </span>
            {need.job_requisition_url ? (
              <a
                href={need.job_requisition_url}
                target="_blank"
                rel="noopener noreferrer"
                className="tw:text-brand-green-0 tw:hover:underline tw:truncate tw:inline-block tw:max-w-[200px] tw:align-bottom"
                onClick={(e) => e.stopPropagation()}
                title={need.job_requisition_url}
              >
                {need.job_requisition_key || 'View'}
              </a>
            ) : (
              <span>{need.job_requisition_key}</span>
            )}
          </div>
        )} */}
      </div>
    </div>
  );
};

export default NeedCard;
